/**
 * ForCustomers — the consolidated Customer Benefits hub.
 *
 * iter139 (Jul 2026) — Owner spec: mirror the simplified navigation
 * pattern of `/for-restaurants` (Restaurants dropdown) and
 * `/partners/partner-benefits` (Partners dropdown) for the customer
 * persona. The "My Account" dropdown collapses from a large panel of
 * 8 benefit tiles + 4 CTAs into three clear items (Customer Benefits
 * → this page, Create Free Account → existing modal, Sign In →
 * existing modal). All benefit content that used to live inside the
 * dropdown is preserved on this page — nothing was rewritten. No
 * backend change, no new registration/login/account/rewards logic.
 *
 * Section order (top → bottom):
 *   1) Customer Benefits — 8 benefit pillars (3 featured + 5 perks)
 *   2) 3 featured Learn More modals (Pay Less / Fast / Rewards)
 *   3) CTA strip — Create Free Account (primary) + Sign In
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Navbar from '../components/Navbar';
import FooterSection from '../components/FooterSection';
import SEO from '../components/SEO';
import { LanguageProvider } from '../context/LanguageContext';
import CustomerAuthModal from '../components/CustomerAuthModal';
import {
  ArrowRight,
  Check,
  DollarSign,
  Zap,
  Gift,
  Heart,
  ClipboardList,
  Users,
  Star,
  Sparkles,
  UserPlus,
  LogIn,
  X,
} from 'lucide-react';

/* ── Customer Benefits — copied verbatim from MyAccountDropdown so the
 * dropdown → page migration is a pure UI relocation (no content
 * rewrite). featured=true benefits get a hero card + Learn More modal. */
const BENEFITS = [
  {
    key: 'pay-less',
    Icon: DollarSign,
    featured: true,
    title: 'Pay Less For Your Food',
    desc: "Many restaurants can offer better prices with NoFee because they don't pay high marketplace commissions.",
    modal: {
      title: 'Pay Less For Your Food',
      intro:
        'Many restaurants increase menu prices on traditional delivery apps to offset high marketplace commissions.\n\nWith NoFee, participating restaurants keep more of their revenue and may offer more competitive pricing directly to customers.',
      points: [
        'Better pricing opportunities',
        'More money stays with local restaurants',
        'Direct restaurant ordering',
        'No marketplace commission pressure',
      ],
    },
  },
  {
    key: 'fast',
    Icon: Zap,
    featured: true,
    title: 'Fast by NoFee',
    desc: 'Delivered On Time. Verified by Real Customers. See which restaurants consistently deliver within their promised time.',
    modal: {
      title: 'Fast by NoFee',
      subtitle: 'Delivered On Time. Verified by Real Customers.',
      intro:
        'Restaurants choose their delivery time.\n\nCustomers confirm whether their order arrived within that promised time.\n\nFast by NoFee helps customers identify restaurants with strong delivery performance.',
      points: [
        'Real customer verification',
        'On-time delivery tracking',
        'Improve customer trust',
        'Reliable delivery recognition',
        'Greater confidence when ordering',
      ],
    },
  },
  {
    key: 'rewards',
    Icon: Gift,
    featured: true,
    title: 'Rewards & Gift Credits',
    desc: 'Earn points, redeem rewards, unlock VIP benefits, and enjoy exclusive offers.',
    modal: {
      title: 'Rewards & Gift Credits',
      intro: 'Create a free account and start earning rewards every time you order.',
      points: [
        'Earn Reward Points',
        'Redeem Gift Credits',
        'Referral Rewards',
        'VIP Rewards',
        'Exclusive Promotions',
        'Faster Checkout',
      ],
    },
  },
  { key: 'favorites', Icon: Heart,         title: 'Save Favorite Restaurants', desc: 'Quick access to your favorite places.' },
  { key: 'orders',    Icon: ClipboardList, title: 'Order History',            desc: 'Reorder previous meals with one click.' },
  { key: 'checkout',  Icon: Zap,           title: 'Faster Checkout',          desc: 'Save time on every future order.' },
  { key: 'referrals', Icon: Users,         title: 'Referral Rewards',         desc: 'Invite friends and earn additional rewards.' },
  { key: 'vip',       Icon: Star,          title: 'VIP Rewards',              desc: 'Unlock premium benefits as your activity grows.' },
];

const ForCustomers = () => {
  const [authMode, setAuthMode] = useState(null); // 'login' | 'register' | null
  const [modalKey, setModalKey] = useState(null);

  // Body-scroll lock while Learn More modal is open.
  useEffect(() => {
    if (!modalKey) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [modalKey]);

  const openAuth = (mode) => setAuthMode(mode);
  const closeAuth = () => setAuthMode(null);

  const featured = BENEFITS.filter((b) => b.featured);
  const perks = BENEFITS.filter((b) => !b.featured);

  return (
    <LanguageProvider>
      <div className="min-h-screen bg-black text-white">
        <SEO
          title="Customer Benefits — Pay Less, Fast Deliveries, Rewards & More"
          description="Everything a customer gets on NoFee in one page. Pay less on your food, verified on-time deliveries, rewards, gift credits, favorites, VIP perks and faster checkout."
          path="/for-customers"
        />
        <Navbar />

        {/* ── 1) BENEFITS PILLARS ────────────────────────────────── */}
        <section
          className="relative pt-32 sm:pt-36 lg:pt-44 pb-14 lg:pb-16"
          data-testid="section-customer-benefits"
        >
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(212,175,55,0.10),transparent_60%)]" />
          <div className="relative max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-10">
              <p className="text-[11px] uppercase tracking-[0.22em] text-gold-400 font-bold">
                Customer Benefits
              </p>
              <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">
                Save money, enjoy trusted deliveries, and earn rewards.
              </h1>
              <p className="mt-4 text-[14.5px] sm:text-[15px] text-white/55 leading-relaxed max-w-2xl mx-auto">
                Everything a NoFee customer gets — pay less on your food, verified on-time deliveries, gift credits, VIP perks, and faster checkout.
              </p>
            </div>

            {/* Featured benefits — 3 cards with Learn More */}
            <p className="text-[11px] uppercase tracking-[0.22em] text-gold-400 font-bold mb-3 text-center">
              Top reasons to join
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-5 mb-10">
              {featured.map(({ key, Icon, title, desc }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setModalKey(key)}
                  data-testid={`for-customers-benefit-${key}`}
                  className="group text-left rounded-2xl border border-gold-500/30 bg-gradient-to-b from-zinc-950 to-black p-6 hover:border-gold-400/60 hover:bg-yellow-500/[0.04] transition-colors"
                >
                  <div className="flex items-center gap-2.5 mb-3">
                    <span className="w-10 h-10 rounded-xl bg-gold-500/12 border border-gold-500/35 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-gold-300" />
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.18em] text-gold-400 font-bold">
                      Featured
                    </span>
                  </div>
                  <h3 className="text-lg font-black text-white leading-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-[13.5px] text-white/65 leading-relaxed">
                    {desc}
                  </p>
                  <span
                    className="mt-4 inline-flex items-center gap-1 text-[11.5px] font-bold text-gold-300 group-hover:text-gold-200"
                    data-testid={`for-customers-benefit-${key}-learn-more`}
                  >
                    Learn More <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </button>
              ))}
            </div>

            {/* Extra perks — 5 informational tiles */}
            <p className="text-[11px] uppercase tracking-[0.22em] text-white/45 font-bold mb-3 text-center">
              Plus more perks
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
              {perks.map(({ key, Icon, title, desc }) => (
                <div
                  key={key}
                  data-testid={`for-customers-benefit-${key}`}
                  className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 hover:border-gold-500/30 transition-colors"
                >
                  <div className="flex items-center gap-2.5 mb-2">
                    <span className="w-9 h-9 rounded-xl bg-white/[0.04] border border-white/[0.08] flex items-center justify-center">
                      <Icon className="w-4 h-4 text-gold-300" />
                    </span>
                    <h3 className="text-base font-black text-white leading-tight">
                      {title}
                    </h3>
                  </div>
                  <p className="text-[13px] text-white/55 leading-relaxed">
                    {desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 2) CTA STRIP — Create Free Account primary + Sign In ── */}
        <section
          className="relative pb-20 lg:pb-24"
          data-testid="section-customer-ctas"
        >
          <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="rounded-3xl border border-gold-500/30 bg-gradient-to-br from-gold-500/[0.06] via-zinc-950 to-black p-8 sm:p-10 text-center shadow-[0_25px_60px_-30px_rgba(212,175,55,0.4)]">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gold-500/40 bg-gold-500/10 mb-5">
                <Sparkles className="w-3.5 h-3.5 text-gold-300" />
                <span className="text-[11px] uppercase tracking-[0.22em] text-gold-300 font-bold">
                  Free forever
                </span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight">
                Ready to order and save?
              </h2>
              <p className="mt-3 max-w-xl mx-auto text-[14.5px] text-white/60 leading-relaxed">
                Create your free account in seconds. No credit card, no subscription — just better food, better prices, and rewards on every order.
              </p>
              <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={() => openAuth('register')}
                  data-testid="for-customers-cta-create"
                  className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl bg-gradient-to-b from-amber-300 to-gold-500 hover:from-amber-200 hover:to-gold-400 text-black font-extrabold text-[14px] tracking-tight transition-all shadow-[0_15px_40px_-15px_rgba(212,175,55,0.55)] hover:-translate-y-0.5"
                >
                  <UserPlus className="w-4 h-4" />
                  Create Free Account
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </button>
                <button
                  type="button"
                  onClick={() => openAuth('login')}
                  data-testid="for-customers-cta-signin"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl border border-white/15 hover:border-gold-500/45 text-white/80 hover:text-gold-200 font-bold text-[13px] transition-all"
                >
                  <LogIn className="w-4 h-4" />
                  Sign In
                </button>
              </div>
            </div>
          </div>
        </section>

        <FooterSection />

        {/* ── Learn More modal — portalled to body so the underlying
            transform / stacking context can never trap it. ─────────── */}
        {modalKey && (() => {
          const b = BENEFITS.find((x) => x.key === modalKey);
          if (!b || !b.modal) return null;
          return createPortal(
            <div
              className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm"
              onClick={() => setModalKey(null)}
              data-testid={`for-customers-modal-${b.key}`}
              style={{ width: '100vw', height: '100dvh' }}
            >
              <div
                className="bg-zinc-950 border border-yellow-500/30 rounded-3xl shadow-2xl overflow-hidden flex flex-col w-[calc(100vw-32px)] sm:w-full sm:max-w-md"
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: 'fixed',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  maxHeight: '90vh',
                }}
              >
                <div className="relative bg-gradient-to-br from-yellow-500/15 via-amber-500/10 to-transparent p-5 sm:p-6 border-b border-white/5 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => setModalKey(null)}
                    className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/8 hover:bg-white/15 flex items-center justify-center transition-colors z-10"
                    aria-label="Close"
                    data-testid="for-customers-modal-close"
                  >
                    <X className="w-5 h-5 text-white" />
                  </button>
                  <div className="w-11 h-11 rounded-xl bg-gold-500/15 border border-gold-500/40 flex items-center justify-center mb-3">
                    <b.Icon className="w-5 h-5 text-gold-300" />
                  </div>
                  <h2 className="text-[22px] sm:text-[24px] font-black text-white leading-[1.1] tracking-tight pr-12">
                    {b.modal.title}
                  </h2>
                  {b.modal.subtitle && (
                    <p className="mt-1 text-[13px] text-yellow-300/90 font-semibold">{b.modal.subtitle}</p>
                  )}
                </div>
                <div className="px-5 sm:px-6 py-4 overflow-y-auto flex-1 min-h-0">
                  <p className="text-[13.5px] text-white/75 leading-relaxed whitespace-pre-line">
                    {b.modal.intro}
                  </p>
                  <ul className="mt-4 space-y-2">
                    {b.modal.points.map((p, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-[13.5px] text-white/85">
                        <Check className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="px-5 sm:px-6 pb-5 pt-3 border-t border-white/5 bg-black/30 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => {
                      setModalKey(null);
                      openAuth('register');
                    }}
                    data-testid="for-customers-modal-create"
                    className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-gradient-to-b from-yellow-300 to-yellow-500 hover:from-yellow-200 hover:to-yellow-400 text-black font-extrabold text-[14px] transition-all"
                  >
                    Create Free Account <ArrowRight className="w-4 h-4" />
                  </button>
                  <p className="mt-2.5 text-center text-[11.5px] text-white/55">
                    Already have an account?{' '}
                    <button
                      type="button"
                      onClick={() => {
                        setModalKey(null);
                        openAuth('login');
                      }}
                      data-testid="for-customers-modal-signin"
                      className="text-yellow-300 hover:text-yellow-200 font-semibold underline underline-offset-2"
                    >
                      Sign In
                    </button>
                  </p>
                </div>
              </div>
            </div>,
            document.body,
          );
        })()}

        {/* Existing customer auth modal — reused, no rewrite. */}
        <CustomerAuthModal
          open={!!authMode}
          initialMode={authMode || 'login'}
          onClose={closeAuth}
        />
      </div>
    </LanguageProvider>
  );
};

export default ForCustomers;
